import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Button, TextField, Grid, Accordion, AccordionSummary,
    AccordionDetails, Table, TableBody, TableCell, TableHead, TableRow,
    TableContainer, Paper, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, Autocomplete, MenuItem
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon, Save as SaveIcon, Edit as EditIcon,
    Delete as DeleteIcon, Add as AddIcon, OpenInNew as OpenInNewIcon,
    Warning as WarningIcon
} from "@mui/icons-material";
import {
    expedienteMediacionesJWT, guardarMediacionRadicacionJWT,
    guardarMediacionAcuerdoJWT, guardarMediacionNotificacionJWT,
    crearAudienciaJWT, editarAudienciaJWT, eliminarAudienciaJWT,
    buscarJudicialesParaMediacionJWT
} from "../../api/endpoints";
import usePermisosModulo from "../../hooks/usePermisosModulo";

const toISO = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
    const d = new Date(s);
    if (!isNaN(d.valueOf())) {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${d.getFullYear()}-${mm}-${dd}`;
    }
    return "";
};

const RESULTADOS = [
    { value: "", label: "-" },
    { value: "Favorable", label: "Favorable" },
    { value: "Desfavorable", label: "Desfavorable" },
];

const ACUERDO_ESTADOS = [
    { value: "", label: "-" },
    { value: "Cierre Via Provincia", label: "Cierre Via Provincia" },
    { value: "Cierre Via Parte", label: "Cierre Via Parte" },
    { value: "En Trámite", label: "En Trámite" },
    { value: "Sin Informe Final", label: "Sin Informe Final" },
    { value: "Acuerdo Aprobado", label: "Acuerdo Aprobado" },
    { value: "Con Decreto", label: "Con Decreto" },
];

export default function MediacionesTab({ idExpediente, setSnackbar, modulo }) {
    // ---------------- Permisos por módulo ----------------
    const { permDe } = usePermisosModulo(modulo);
    // Sub-pestaña Radicación de mediaciones → función "PESTAÑA RADICACION" (Id 4)
    const permRad = permDe("PESTAÑA RADICACION");
    // Acuerdo, Notificación, Audiencias → función "PESTAÑA ACUERDO" (Id 15)
    const permAcuerdo = permDe("PESTAÑA ACUERDO");

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    // Secciones expandidas
    const [expanded, setExpanded] = useState({ radicacion: true, acuerdo: false, notificacion: false });

    // Modos edición
    const [editRad, setEditRad] = useState(false);
    const [editAcuerdo, setEditAcuerdo] = useState(false);
    const [editNotif, setEditNotif] = useState(false);

    // Forms
    const [radForm, setRadForm] = useState({});
    const [acuerdoForm, setAcuerdoForm] = useState({});
    const [notifForm, setNotifForm] = useState({});

    // Audiencias
    const [audienciaDialog, setAudienciaDialog] = useState(false);
    const [audienciaForm, setAudienciaForm] = useState({ tipo: "", fecha: "", descripcion: "" });
    const [audienciaEditId, setAudienciaEditId] = useState(null);
    const [saving, setSaving] = useState(false);

    // Dialog confirmación eliminar
    const [eliminarDialog, setEliminarDialog] = useState({ open: false, onConfirm: null });

    // Búsqueda expediente judicial
    const [judicialSearch, setJudicialSearch] = useState("");
    const [judicialOptions, setJudicialOptions] = useState([]);
    const [judicialSelected, setJudicialSelected] = useState(null);
    const [searchingJudicial, setSearchingJudicial] = useState(false);

    const msg = useCallback((message, severity = "success") => setSnackbar({ open: true, message, severity }), [setSnackbar]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await expedienteMediacionesJWT(idExpediente);
            if (resp?.ok) setData(resp);
        } catch (e) {
            msg("Error al cargar mediaciones", "error");
        } finally {
            setLoading(false);
        }
    }, [idExpediente, msg]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Inicializar forms cuando llegan datos
    useEffect(() => {
        if (!data) return;
        if (data.radicacion) {
            setRadForm({
                id_expediente_judicial: data.radicacion.id_expediente_judicial || "",
                id_area: data.radicacion.id_area || "",
                autos_m: data.radicacion.autos_m || "",
                autos_1c: data.radicacion.autos_1c || "",
                autos_2c: data.radicacion.autos_2c || "",
                autos_3c: data.radicacion.autos_3c || "",
                resultado_m: data.radicacion.resultado_m || "",
                resultado_1c: data.radicacion.resultado_1c || "",
                resultado_2c: data.radicacion.resultado_2c || "",
                resultado_3c: data.radicacion.resultado_3c || "",
            });
        }
        if (data.acuerdo) {
            setAcuerdoForm({
                num_decreto: data.acuerdo.num_decreto || "",
                fecha_decreto: toISO(data.acuerdo.fecha_decreto),
                fecha_acuerdo: toISO(data.acuerdo.fecha_acuerdo),
                monto_demandado: data.acuerdo.monto_demandado || "",
                monto_convenido: data.acuerdo.monto_convenido || "",
                cuotas_acordadas: data.acuerdo.cuotas_acordadas || "",
                primer_vencimiento: toISO(data.acuerdo.primer_vencimiento),
                honorarios_mediador: data.acuerdo.honorarios_mediador || "",
                objeto: data.acuerdo.objeto || "",
                id_organismo: data.acuerdo.id_organismo || "",
                fecha_homologacion: toISO(data.acuerdo.fecha_homologacion),
                fecha_notificacion: toISO(data.acuerdo.fecha_notificacion),
                fecha_limite: toISO(data.acuerdo.fecha_limite),
            });
        }
        if (data.notificacion) {
            setNotifForm({
                fecha_ingreso_vista: toISO(data.notificacion.fecha_ingreso_vista),
                fecha_ingreso_cedula: toISO(data.notificacion.fecha_ingreso_cedula),
                formulario: data.notificacion.formulario || "",
                reclamo_economico: data.notificacion.reclamo_economico || "",
                objeto: data.notificacion.objeto || "",
                dictamen: data.notificacion.dictamen || "",
                instrucciones: data.notificacion.instrucciones || "",
                observaciones: data.notificacion.observaciones || "",
                acuerdo: data.notificacion.acuerdo || "",
            });
        }
        // Inicializar judicial seleccionado
        if (data.judicial && data.radicacion?.id_expediente_judicial) {
            const j = data.judicial;
            setJudicialSelected({
                Id: data.radicacion.id_expediente_judicial,
                Caratula: j.Caratula || "",
                Autos_1_Instancia: j.Autos || "",
                Juzgado: j.Juzgado || "",
            });
        } else {
            setJudicialSelected(null);
        }
    }, [data]);

    // Búsqueda de expedientes judiciales con debounce
    useEffect(() => {
        if (!judicialSearch || judicialSearch.length < 2) {
            setJudicialOptions([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearchingJudicial(true);
            try {
                const resp = await buscarJudicialesParaMediacionJWT(judicialSearch);
                if (resp?.ok) setJudicialOptions(resp.expedientes || []);
            } catch { /* ignore */ }
            finally { setSearchingJudicial(false); }
        }, 400);
        return () => clearTimeout(timer);
    }, [judicialSearch]);

    // --- Guardar Radicación ---
    const handleSaveRad = async () => {
        setSaving(true);
        try {
            const resp = await guardarMediacionRadicacionJWT(idExpediente, radForm);
            if (resp?.ok) { msg("Radicación de mediación guardada"); setEditRad(false); fetchData(); }
            else msg(resp?.mensaje || "Error al guardar", "error");
        } catch { msg("Error al guardar radicación", "error"); }
        finally { setSaving(false); }
    };

    // --- Guardar Acuerdo ---
    const handleSaveAcuerdo = async () => {
        if (!data?.radicacion) { msg("Debe completar primero la sección de Radicación", "warning"); return; }
        setSaving(true);
        try {
            const resp = await guardarMediacionAcuerdoJWT(idExpediente, acuerdoForm);
            if (resp?.ok) { msg("Acuerdo de mediación guardado"); setEditAcuerdo(false); fetchData(); }
            else msg(resp?.mensaje || "Error al guardar", "error");
        } catch { msg("Error al guardar acuerdo", "error"); }
        finally { setSaving(false); }
    };

    // --- Guardar Notificación ---
    const handleSaveNotif = async () => {
        setSaving(true);
        try {
            const resp = await guardarMediacionNotificacionJWT(idExpediente, notifForm);
            if (resp?.ok) { msg("Notificación de mediación guardada"); setEditNotif(false); fetchData(); }
            else msg(resp?.mensaje || "Error al guardar", "error");
        } catch { msg("Error al guardar notificación", "error"); }
        finally { setSaving(false); }
    };

    // --- Audiencias CRUD ---
    const handleSaveAudiencia = async () => {
        if (!audienciaForm.fecha || !audienciaForm.descripcion) {
            msg("Fecha y descripción son requeridos", "warning"); return;
        }
        setSaving(true);
        try {
            const resp = audienciaEditId
                ? await editarAudienciaJWT(idExpediente, audienciaEditId, audienciaForm)
                : await crearAudienciaJWT(idExpediente, audienciaForm);
            if (resp?.ok) { msg(audienciaEditId ? "Audiencia actualizada" : "Audiencia creada"); setAudienciaDialog(false); fetchData(); }
            else msg(resp?.mensaje || "Error", "error");
        } catch { msg("Error al guardar audiencia", "error"); }
        finally { setSaving(false); }
    };

    const handleDeleteAudiencia = (aud) => {
        setEliminarDialog({
            open: true,
            onConfirm: async () => {
                setEliminarDialog(prev => ({ ...prev, open: false }));
                try {
                    const resp = await eliminarAudienciaJWT(idExpediente, aud.id);
                    if (resp?.ok) { msg("Audiencia eliminada"); fetchData(); }
                    else msg(resp?.mensaje || "Error", "error");
                } catch { msg("Error al eliminar audiencia", "error"); }
            }
        });
    };

    const openAudienciaNew = () => {
        setAudienciaForm({ tipo: "", fecha: "", descripcion: "" });
        setAudienciaEditId(null);
        setAudienciaDialog(true);
    };

    const openAudienciaEdit = (aud) => {
        setAudienciaForm({ tipo: aud.tipo, fecha: toISO(aud.fecha), descripcion: aud.descripcion });
        setAudienciaEditId(aud.id);
        setAudienciaDialog(true);
    };

    if (loading) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

    const areas = data?.areas || [];
    const audiencias = data?.audiencias || [];
    const judicial = data?.judicial;
    const organismos = data?.organismos || [];

    const fieldProps = (disabled) => ({ size: "small", fullWidth: true, disabled, InputLabelProps: { shrink: true } });

    const accordionHeaderSx = {
        bgcolor: "primary.main",
        color: "primary.contrastText",
        minHeight: 40,
        "&.Mui-expanded": { minHeight: 40 },
        "& .MuiAccordionSummary-content": { my: 0.5 },
        "& .MuiSvgIcon-root": { color: "primary.contrastText" },
        borderRadius: 0,
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* ===== RADICACIÓN ===== */}
            <Accordion expanded={expanded.radicacion} onChange={() => setExpanded(p => ({ ...p, radicacion: !p.radicacion }))} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionHeaderSx}>
                    <Typography variant="subtitle2" fontWeight="bold">Radicación</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box mb={1} display="flex" justifyContent="flex-end">
                        {!editRad ? (
                            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditRad(true)} disabled={!permRad.update}>Editar</Button>
                        ) : (
                            <Box display="flex" gap={1}>
                                <Button size="small" onClick={() => { setEditRad(false); fetchData(); }}>Cancelar</Button>
                                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveRad} disabled={saving}>Guardar</Button>
                            </Box>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <Autocomplete
                                options={judicialOptions}
                                getOptionLabel={(o) => {
                                    const autos = o.Autos_1_Instancia || o.Autos_2_Instancia || "";
                                    return `${autos ? autos + " - " : ""}${o.Caratula || "Sin carátula"}`;
                                }}
                                value={judicialSelected}
                                onChange={(_, v) => {
                                    setJudicialSelected(v);
                                    setRadForm(p => ({ ...p, id_expediente_judicial: v?.Id || "" }));
                                }}
                                onInputChange={(_, v, reason) => {
                                    if (reason === "input") setJudicialSearch(v);
                                }}
                                isOptionEqualToValue={(o, v) => o.Id === v.Id}
                                disabled={!editRad}
                                loading={searchingJudicial}
                                filterOptions={(x) => x}
                                noOptionsText={judicialSearch.length < 2 ? "Escribí al menos 2 caracteres..." : "Sin resultados"}
                                renderOption={(props, o) => (
                                    <li {...props} key={o.Id}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {o.Autos_1_Instancia || o.Autos_2_Instancia || "Sin autos"} - {o.Caratula || "Sin carátula"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {o.Juzgado || "Sin juzgado"} · {o.Modulo || ""}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Expediente Judicial Vinculado" size="small"
                                        placeholder="Buscar por autos o carátula..."
                                        slotProps={{ input: { ...params.InputProps, endAdornment: (
                                            <>
                                                {searchingJudicial ? <CircularProgress size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ) } }}
                                    />
                                )}
                            />
                        </Grid>
                        {/* Info del judicial vinculado */}
                        {judicial && radForm.id_expediente_judicial && (
                            <Grid size={{ xs: 12 }}>
                                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "grey.50", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Datos del Expediente Judicial Vinculado</Typography>
                                        <Typography variant="body2"><strong>Carátula:</strong> {judicial.Caratula || "-"}</Typography>
                                        <Typography variant="body2"><strong>Juzgado:</strong> {judicial.Juzgado || "-"}</Typography>
                                        <Typography variant="body2"><strong>Mediador:</strong> {judicial.Mediador || "-"}</Typography>
                                        <Typography variant="body2"><strong>Autos:</strong> {judicial.Autos || "-"}</Typography>
                                    </Box>
                                    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                                        onClick={() => window.open(`/expediente/judicial/${radForm.id_expediente_judicial}`, '_blank')}>
                                        Abrir
                                    </Button>
                                </Paper>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                options={areas}
                                getOptionLabel={(o) => o.area || ""}
                                value={areas.find(a => a.id === Number(radForm.id_area)) || null}
                                onChange={(_, v) => setRadForm(p => ({ ...p, id_area: v?.id || "" }))}
                                disabled={!editRad}
                                renderInput={(params) => <TextField {...params} label="Área de Origen F.E." size="small" />}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Instancias</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Instancia</TableCell>
                                    <TableCell>Nº Autos</TableCell>
                                    <TableCell>Resultado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { label: "Mediación", autosKey: "autos_m", resKey: "resultado_m" },
                                    { label: "1ra Conciliación", autosKey: "autos_1c", resKey: "resultado_1c" },
                                    { label: "2da Conciliación", autosKey: "autos_2c", resKey: "resultado_2c" },
                                    { label: "3ra Conciliación", autosKey: "autos_3c", resKey: "resultado_3c" },
                                ].map((row, idx) => (
                                    <TableRow key={row.autosKey} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                        <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                                        <TableCell>
                                            <TextField value={radForm[row.autosKey] || ""} placeholder="000000/00"
                                                onChange={e => setRadForm(p => ({ ...p, [row.autosKey]: e.target.value }))}
                                                {...fieldProps(!editRad)} inputProps={{ maxLength: 20 }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField select value={radForm[row.resKey] || ""}
                                                onChange={e => setRadForm(p => ({ ...p, [row.resKey]: e.target.value }))}
                                                {...fieldProps(!editRad)}>
                                                {RESULTADOS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                            </TextField>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* ===== ACUERDO ===== */}
            <Accordion expanded={expanded.acuerdo} onChange={() => setExpanded(p => ({ ...p, acuerdo: !p.acuerdo }))} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionHeaderSx}>
                    <Typography variant="subtitle2" fontWeight="bold">Acuerdo</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box mb={1} display="flex" justifyContent="flex-end">
                        {!editAcuerdo ? (
                            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditAcuerdo(true)} disabled={!permAcuerdo.update}>Editar</Button>
                        ) : (
                            <Box display="flex" gap={1}>
                                <Button size="small" onClick={() => { setEditAcuerdo(false); fetchData(); }}>Cancelar</Button>
                                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAcuerdo} disabled={saving}>Guardar</Button>
                            </Box>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Nº de Decreto" value={acuerdoForm.num_decreto || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, num_decreto: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Decreto" type="date" value={acuerdoForm.fecha_decreto || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, fecha_decreto: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Acuerdo" type="date" value={acuerdoForm.fecha_acuerdo || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, fecha_acuerdo: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Monto Demandado" type="number" value={acuerdoForm.monto_demandado || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, monto_demandado: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Monto Convenido" type="number" value={acuerdoForm.monto_convenido || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, monto_convenido: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Cuotas Acordadas" type="number" value={acuerdoForm.cuotas_acordadas || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, cuotas_acordadas: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Primer Vencimiento" type="date" value={acuerdoForm.primer_vencimiento || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, primer_vencimiento: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Honorarios Mediador" type="number" value={acuerdoForm.honorarios_mediador || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, honorarios_mediador: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Homologación" type="date" value={acuerdoForm.fecha_homologacion || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, fecha_homologacion: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Notificación" type="date" value={acuerdoForm.fecha_notificacion || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, fecha_notificacion: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Límite Cumplimiento" type="date" value={acuerdoForm.fecha_limite || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, fecha_limite: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select label="Organismo que efectúa el pago" value={acuerdoForm.id_organismo || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, id_organismo: e.target.value }))} {...fieldProps(!editAcuerdo)}>
                                <MenuItem value="">-</MenuItem>
                                {organismos.map(o => <MenuItem key={o.id} value={o.id}>{o.reparticion}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Objeto" multiline minRows={3} value={acuerdoForm.objeto || ""}
                                onChange={e => setAcuerdoForm(p => ({ ...p, objeto: e.target.value }))} {...fieldProps(!editAcuerdo)} />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* ===== NOTIFICACIONES ===== */}
            <Accordion expanded={expanded.notificacion} onChange={() => setExpanded(p => ({ ...p, notificacion: !p.notificacion }))} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionHeaderSx}>
                    <Typography variant="subtitle2" fontWeight="bold">Notificaciones</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box mb={1} display="flex" justifyContent="flex-end">
                        {!editNotif ? (
                            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditNotif(true)} disabled={!permAcuerdo.update}>Editar</Button>
                        ) : (
                            <Box display="flex" gap={1}>
                                <Button size="small" onClick={() => { setEditNotif(false); fetchData(); }}>Cancelar</Button>
                                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveNotif} disabled={saving}>Guardar</Button>
                            </Box>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Ingreso Vista" type="date" value={notifForm.fecha_ingreso_vista || ""}
                                onChange={e => setNotifForm(p => ({ ...p, fecha_ingreso_vista: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Fecha Ingreso Cédula" type="date" value={notifForm.fecha_ingreso_cedula || ""}
                                onChange={e => setNotifForm(p => ({ ...p, fecha_ingreso_cedula: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Reclamo Económico" type="number" value={notifForm.reclamo_economico || ""}
                                onChange={e => setNotifForm(p => ({ ...p, reclamo_economico: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Formulario" value={notifForm.formulario || ""}
                                onChange={e => setNotifForm(p => ({ ...p, formulario: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Dictamen" value={notifForm.dictamen || ""}
                                onChange={e => setNotifForm(p => ({ ...p, dictamen: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select label="Estado del Acuerdo" value={notifForm.acuerdo || ""}
                                onChange={e => setNotifForm(p => ({ ...p, acuerdo: e.target.value }))} {...fieldProps(!editNotif)}>
                                {ACUERDO_ESTADOS.map(e => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Observaciones" value={notifForm.observaciones || ""}
                                onChange={e => setNotifForm(p => ({ ...p, observaciones: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Objeto" multiline minRows={2} value={notifForm.objeto || ""}
                                onChange={e => setNotifForm(p => ({ ...p, objeto: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Instrucciones" multiline minRows={2} value={notifForm.instrucciones || ""}
                                onChange={e => setNotifForm(p => ({ ...p, instrucciones: e.target.value }))} {...fieldProps(!editNotif)} />
                        </Grid>
                    </Grid>

                    {/* Audiencias */}
                    <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" fontWeight="bold">Audiencias</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={openAudienciaNew} disabled={!permAcuerdo.insert}>Nueva</Button>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Descripción</TableCell>
                                    <TableCell>Responsable</TableCell>
                                    <TableCell align="center" width={80}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {audiencias.length === 0 ? (
                                    <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">Sin audiencias</Typography></TableCell></TableRow>
                                ) : audiencias.map((aud, idx) => (
                                    <TableRow key={aud.id} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                        <TableCell>{aud.tipo || "-"}</TableCell>
                                        <TableCell>{aud.fecha ? new Date(aud.fecha).toLocaleDateString("es-AR") : "-"}</TableCell>
                                        <TableCell>{aud.descripcion}</TableCell>
                                        <TableCell>{aud.responsable}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => openAudienciaEdit(aud)} disabled={!permAcuerdo.update}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDeleteAudiencia(aud)} disabled={!permAcuerdo.delete}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* Dialog Audiencia */}
            <Dialog open={audienciaDialog} onClose={() => setAudienciaDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{audienciaEditId ? "Editar Audiencia" : "Nueva Audiencia"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Tipo" fullWidth size="small" value={audienciaForm.tipo}
                                onChange={e => setAudienciaForm(p => ({ ...p, tipo: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Fecha" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
                                value={audienciaForm.fecha} onChange={e => setAudienciaForm(p => ({ ...p, fecha: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Descripción" fullWidth size="small" multiline minRows={2}
                                value={audienciaForm.descripcion} onChange={e => setAudienciaForm(p => ({ ...p, descripcion: e.target.value }))} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAudienciaDialog(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveAudiencia} disabled={saving}>Guardar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog confirmación eliminar */}
            <Dialog
                open={eliminarDialog.open}
                onClose={() => setEliminarDialog(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
                    <WarningIcon /> Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography>¿Estás seguro de que querés eliminar esta audiencia?</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEliminarDialog(prev => ({ ...prev, open: false }))} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={eliminarDialog.onConfirm} variant="contained" color="error">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
